import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import CollapseWrapper from '../../Global/CollapseWrapper';
import TextFieldInput from '../../Global/Inputs/TextField';
import TimeFieldInput from '../../Global/Inputs/TimeField';
import combineStyles from '../../../utils/combineStyles';
import TypographyStyles from '../../../styles/typography';
import LayoutStyles from '../../../styles/layout';
import SpacingStyles from '../../../styles/helpers/spacing';
import OutlinedSelectInput from '../../Global/Inputs/OutlinedSelect';
import ButtonStyles from '../../../styles/button';
import Button from '@material-ui/core/Button';
import { withNamespaces } from 'react-i18next';
import { FastField, Field } from 'formik';
import LocationStyles from './__styles__/Location.styles';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { STATES, COUNTRIES } from '../../../constants/dropdowns';
import { geoCodeAddress, searchByAddress } from '../../../common/maps';
import MapModal from '../../Global/Modals/Maps';
import MapModalError from '../../Global/Modals/MapError';
import Autocomplete from '../../Global/Inputs/Autocomplete';
import Grid from '@material-ui/core/Grid';
import { searchLocations } from '../../../utils/Shipment';
import {
  updateAddressFields,
  getContactSuggestions,
  getContactById
} from './helpers';
import GridItem from './../../Global/GridItem';
import { SHIPMENT_DIRECTION } from './../../../constants/constants';
import { wasTouched } from '../../../utils/Validation';
import PhoneField from './../../Global/Inputs/PhoneField';
import { AppContext } from '../../../AppProvider';
export class Location extends PureComponent {
  static propTypes = {
    locationType: PropTypes.oneOf(['pickup', 'delivery']).isRequired,
    label: PropTypes.string.isRequired,
    locations: PropTypes.array.isRequired,
    defaultLocation: PropTypes.object,
    defaultDockOpen: PropTypes.number, //overridden by defaultLocation or selected location
    defaultDockClose: PropTypes.number, //overridden by defaultLocation or selected location
    contacts: PropTypes.array.isRequired,
    values: PropTypes.object,
    handleValidateField: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    isNewShipment: PropTypes.bool.isRequired,
    fromQuickRate: PropTypes.bool.isRequired,
    shipmentDirection: PropTypes.string,
    touched: PropTypes.object
  };

  state = {
    expanded: false,
    mapError: false,
    geoCodedAddress: {},
    mapModalOpen: false,
    locationName: '',
    firstName: ''
  };
  handleChange = () => {
    const { expanded } = this.state;
    this.setState({
      expanded: !expanded
    });
  };
  _handleMapButtonClick = async address => {
    try {
      const coordinates = await geoCodeAddress(address);

      this.setState({
        geoCodedAddress: {
          ...address,
          lat: coordinates.position.lat,
          long: coordinates.position.lon
        },
        mapModalOpen: true
      });
    } catch (error) {
      this.setState({ mapError: true });
    }
  };
  _handleMapModalClose = () => {
    this.setState({ mapModalOpen: false });
  };
  _handleMapModalErrorClose = () => {
    this.setState({ mapError: false, geoCodedAddress: {} });
  };
  _shouldBeExpanded = () => {
    const {
      isNewShipment,
      shipmentDirection,
      locationType,
      defaultLocation
    } = this.props;
    const { expanded } = this.state;
    const stops = {
      pickup: false,
      delivery: false
    };
    if (isNewShipment) {
      // If no default location then we want to expand both.
      if (!defaultLocation) {
        stops.pickup = true;
        stops.delivery = true;
      } else {
        //case statement.  we will have one variable that has both stops expand and collapse values
        switch (shipmentDirection) {
          case SHIPMENT_DIRECTION.OUTBOUND:
            stops.delivery = true;
            break;
          case SHIPMENT_DIRECTION.INBOUND:
            stops.pickup = true;
            break;
          case SHIPMENT_DIRECTION.SHUTTLE:
            stops.delivery = true;
            break;
          case SHIPMENT_DIRECTION.DROP:
            stops.pickup = true;
            stops.delivery = true;
            break;
          default:
        }
      }
      return stops[locationType];
    }

    return expanded;
  };
  componentDidMount() {
    const { values, fromQuickRate } = this.props;
    const {
      setFieldValue,
      locationType,
      defaultDockOpen,
      defaultDockClose
    } = this.props;
    setFieldValue(`${locationType}.openFromTimeLocal`, defaultDockOpen);
    setFieldValue(`${locationType}.openToTimeLocal`, defaultDockClose);

    const shouldDefaultLocation = this._shouldDefaultLocation();
    if (shouldDefaultLocation) {
      this._setDefaultLocation();
    }
    this.setState({
      locationName: _get(values, 'locationName', '') || '',
      firstName: _get(values, 'contactInfo.firstName', '') || '',
      expanded:
        fromQuickRate && !shouldDefaultLocation
          ? true
          : this._shouldBeExpanded()
    });
  }
  componentDidUpdate(prevProps) {
    if (
      this.props.shipmentDirection &&
      prevProps.shipmentDirection !== this.props.shipmentDirection
    ) {
      this._shouldUpdateLocation();
      this.setState({
        expanded: this._shouldBeExpanded()
      });
    }
    if (
      _get(prevProps, 'values.contactInfo.firstName', '') !==
      _get(this.props, 'values.contactInfo.firstName', '')
    ) {
      this.setState({
        firstName: _get(this.props, 'values.contactInfo.firstName', '')
      });
    }
    if (prevProps.values.locationName !== this.props.values.locationName) {
      this.setState({ locationName: this.props.values.locationName || '' });
    }
  }

  _shouldDefaultLocation = () => {
    // We want to default the location if:
    // 1) It's a new shipment and not from quick rate
    // 2) It's a new shipment and from quick rate and the default location's ZIP matches what they entered in quick rate keeping
    //    in mind that the shipment direction matters in relation to the pickup vs. delivery
    const {
      values,
      isNewShipment,
      fromQuickRate,
      defaultLocation
    } = this.props;
    let shouldDefault = false;
    if (isNewShipment && defaultLocation) {
      if (!fromQuickRate) {
        shouldDefault = true;
      } else {
        // We've got a quick rate transfer in progress.
        // If the default location's ZIP matches the entered ZIP
        // then let things default.  The other logic in the default flow will figure
        // out what should be update, if anything, based on shipment direction and
        // pickup vs. delivery.
        const compareZip = _get(values, `locationAddress.postalCode`, '') || '';

        if (defaultLocation.address.postalCode === compareZip) {
          shouldDefault = true;
        }
      }
    }

    return shouldDefault;
  };

  _shouldUpdateLocation = () => {
    const {
      locationType,
      shipmentDirection,
      defaultLocation,
      touched,
      isNewShipment,
      defaultDockOpen,
      defaultDockClose
    } = this.props;
    const touchedStops = {
      pickup: wasTouched(touched, 'pickup.locationAddress'),
      delivery: wasTouched(touched, 'delivery.locationAddress')
    };

    const stops = {
      pickup: {},
      delivery: {}
    };
    const {
      orgInfo: {
        config: { defaultCountry }
      }
    } = this.context.globalProps;

    const emptyLocation = {
      data: {
        address: {
          address1: '',
          address2: '',
          city: '',
          state: null,
          postalCode: null,
          country: defaultCountry
        },
        openFromTimeLocal: defaultDockOpen,
        openToTimeLocal: defaultDockClose,
        name: '',
        contactInfo: {
          firstName: '',
          lastName: '',
          email: '',
          phone: ''
        }
      }
    };
    if (isNewShipment) {
      switch (shipmentDirection) {
        case SHIPMENT_DIRECTION.OUTBOUND:
        case SHIPMENT_DIRECTION.SHUTTLE:
          stops.pickup = defaultLocation
            ? { data: defaultLocation }
            : emptyLocation;
          stops.delivery = emptyLocation;
          break;
        case SHIPMENT_DIRECTION.INBOUND:
          stops.pickup = emptyLocation;
          stops.delivery = defaultLocation
            ? { data: defaultLocation }
            : emptyLocation;
          break;
        case SHIPMENT_DIRECTION.DROP:
          stops.pickup = emptyLocation;
          stops.delivery = emptyLocation;
          break;
        default:
          break;
      }

      if (!touchedStops[locationType]) {
        this._handleLocationSelected(stops[locationType], locationType);
      }
    }
  };
  _setDefaultLocation = () => {
    const {
      locationType,
      shipmentDirection,
      defaultLocation = null
    } = this.props;

    let stopToUpdate = 'pickup';

    if (defaultLocation) {
      switch (shipmentDirection) {
        case SHIPMENT_DIRECTION.OUTBOUND:
        case SHIPMENT_DIRECTION.SHUTTLE:
          stopToUpdate = 'pickup';
          break;
        case SHIPMENT_DIRECTION.INBOUND:
          stopToUpdate = 'delivery';
          break;
        default:
      }
      if (locationType === stopToUpdate) {
        this._handleLocationSelected({ data: defaultLocation }, locationType);
      }
    }
  };
  _getSuggestions = async value => {
    const suggestionResults = await searchByAddress(value);
    const suggestions = suggestionResults.map(result => {
      return {
        label: result.freeformAddress,
        ...result
      };
    });

    return suggestions;
  };
  _onAddressSelected = address => {
    const { locationType, setFieldValue } = this.props;
    const addressObject = {
      address1: `${address.streetNameAndNumber}`,
      address2: '',
      city: address.municipality,
      state: address.countrySubdivision,
      postalCode: address.postalCode,
      country: address.countryCode
    };
    updateAddressFields(locationType, setFieldValue, addressObject);
  };
  _onAddressChanged = change => {
    const { locationType, setFieldValue } = this.props;
    setFieldValue(`${locationType}.locationAddress.address1`, change);
  };
  _getLocationSuggestions = async value => {
    const { locations } = this.props;
    const suggestions = searchLocations(locations, value);
    return Promise.resolve(suggestions);
  };
  _handleChange = (field, value) => {
    this.setState({ [field]: value });
  };
  _handleLocationSelected = (location, locationType) => {
    const { setFieldValue, contacts } = this.props;
    const blankContact = {
      data: {
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      }
    };
    //contact must be blanked out first in case this changed location has no primary contact.
    this._handleContactSelected(blankContact);

    const {
      data: {
        address,
        name,
        openFromTimeLocal,
        openToTimeLocal,
        primaryContactId,
        pickupNotes,
        deliveryNotes
      }
    } = location;
    if (primaryContactId) {
      const contact = getContactById(primaryContactId, contacts);
      // Possible the primary contact for the location is
      // currently inactive, so let's not blow up
      if (contact) {
        this._handleContactSelected(contact);
      }
    }
    setFieldValue(`${locationType}.locationName`, name);
    setFieldValue(`${locationType}.openFromTimeLocal`, openFromTimeLocal);
    setFieldValue(`${locationType}.openToTimeLocal`, openToTimeLocal);
    setFieldValue(`${locationType}.pickupNotes`, pickupNotes);
    setFieldValue(`${locationType}.deliveryNotes`, deliveryNotes);

    updateAddressFields(locationType, setFieldValue, address);
  };

  _handleContactSelected = ({ data: contact }) => {
    const { locationType, setFieldValue } = this.props;
    setFieldValue(`${locationType}.contactInfo.firstName`, contact.firstName);
    setFieldValue(`${locationType}.contactInfo.lastName`, contact.lastName);
    setFieldValue(`${locationType}.contactInfo.phone`, contact.phone);
    setFieldValue(`${locationType}.contactInfo.email`, contact.email);
  };
  render() {
    const {
      classes,
      handleChange,
      t,
      label,
      locationType,
      values,
      contacts,
      handleValidateField,
      setFieldValue
    } = this.props;

    const {
      locationAddress = {},
      contactInfo = {},
      openFromTimeLocal,
      openToTimeLocal,
      pickupNotes,
      deliveryNotes
    } = values;
    const { lastName = '', email = '', phone = '' } = contactInfo;
    const {
      mapModalOpen,
      geoCodedAddress,
      mapError,
      locationName,
      firstName,
      expanded
    } = this.state;

    const formattedAddress = locationAddress.address1
      ? `${locationName}, ${locationAddress.address1}, ${
          locationAddress.city
        } ${locationAddress.state} ${locationAddress.postalCode}`
      : '';
    const country = _get(locationAddress, 'country');
    return (
      <CollapseWrapper
        classes={{
          root: classes.wrapperNoPadding,
          header: classes.panelHeader,
          expandButton: classes.expandButton,
          bodyRoot: classes.collapsePanelBodyRoot
        }}
        expanded={expanded}
        header={({ expanded }) => (
          <div className={classes.titleWrapper}>
            <h2 className={classnames(classes.headline8, classes.title)}>
              {label}
            </h2>
            {!expanded && !_isEmpty(locationAddress) && (
              <p
                className={classnames(
                  classes.formattedAddress,
                  classes.body2,
                  classes.noMargin,
                  {
                    [classes.hidden]: expanded
                  }
                )}
              >
                {formattedAddress}
              </p>
            )}
          </div>
        )}
        body={
          <Fragment>
            <div className={classes.titleContentDivider} />
            <Grid
              container
              spacing={16}
              classes={{ container: classes.gridContainer }}
            >
              <Grid item lg={12}>
                <h4
                  className={classnames(
                    classes.sectionHeading,
                    classes.headline7
                  )}
                >
                  {t('Location Details')}
                </h4>
              </Grid>
              <GridItem lg={12}>
                <Field
                  name={`${locationType}.locationName`}
                  render={({ field, form }) => {
                    return (
                      <Autocomplete
                        value={locationName}
                        validateField={handleValidateField}
                        getSuggestions={this._getLocationSuggestions}
                        onSuggestionSelected={location => {
                          this._handleLocationSelected(location, locationType);
                        }}
                        label={t('Location Name')}
                        onChange={change => {
                          this._handleChange('locationName', change);
                          setFieldValue(`${locationType}.locationName`, change);
                        }}
                        field={field}
                        form={form}
                      />
                    );
                  }}
                />
              </GridItem>
              <GridItem lg={6} md={6}>
                <FastField
                  name={`${locationType}.locationAddress.address1`}
                  render={({ field, form }) => {
                    return (
                      <Autocomplete
                        getSuggestions={this._getSuggestions}
                        onSuggestionSelected={this._onAddressSelected}
                        label={t('Address Line 1')}
                        value={locationAddress.address1 || ''}
                        onChange={this._onAddressChanged}
                        validateField={handleValidateField}
                        field={field}
                        form={form}
                      />
                    );
                  }}
                />
              </GridItem>
              <GridItem lg={6} md={6}>
                <FastField
                  value={locationAddress.address2 || ''}
                  name={`${locationType}.locationAddress.address2`}
                  label={t('Address Line 2')}
                  onChange={handleChange}
                  component={TextFieldInput}
                />
              </GridItem>
              <GridItem lg={3} md={6}>
                <FastField
                  name={`${locationType}.locationAddress.country`}
                  onChange={handleChange}
                  render={({ field, form }) => {
                    return (
                      <OutlinedSelectInput
                        value={locationAddress.country}
                        field={field}
                        form={form}
                        label={t('Country')}
                        options={COUNTRIES}
                        multiple={false}
                        validateField={handleValidateField}
                      />
                    );
                  }}
                />
              </GridItem>
              <GridItem lg={3} md={3}>
                <FastField
                  value={locationAddress.city || ''}
                  name={`${locationType}.locationAddress.city`}
                  label={t('City')}
                  onChange={handleChange}
                  component={TextFieldInput}
                  validateField={handleValidateField}
                />
              </GridItem>
              <GridItem lg={3} md={3}>
                <Field
                  name={`${locationType}.locationAddress.state`}
                  onChange={handleChange}
                  render={({ field, form }) => {
                    return (
                      <OutlinedSelectInput
                        value={locationAddress.state || null}
                        field={field}
                        form={form}
                        label={t('State')}
                        options={STATES[country] || []}
                        multiple={false}
                        validateField={handleValidateField}
                      />
                    );
                  }}
                />
              </GridItem>
              <GridItem lg={3} md={3}>
                <FastField
                  value={locationAddress.postalCode || ''}
                  name={`${locationType}.locationAddress.postalCode`}
                  label={t('Zip')}
                  onChange={handleChange}
                  validateField={handleValidateField}
                  component={TextFieldInput}
                />
              </GridItem>

              <GridItem lg={3} md={3}>
                <FastField
                  name={`${locationType}.openFromTimeLocal`}
                  render={({ field, form }) => {
                    return (
                      <TimeFieldInput
                        value={openFromTimeLocal}
                        onChange={handleChange}
                        field={field}
                        form={form}
                        validateField={handleValidateField}
                        label={t('Dock Opens')}
                      />
                    );
                  }}
                />
              </GridItem>
              <GridItem item lg={3} md={3}>
                <FastField
                  name={`${locationType}.openToTimeLocal`}
                  onChange={handleChange}
                  render={({ field, form }) => {
                    return (
                      <TimeFieldInput
                        value={openToTimeLocal}
                        field={field}
                        form={form}
                        validateField={handleValidateField}
                        label={t('Dock Closes')}
                      />
                    );
                  }}
                />
              </GridItem>

              <GridItem lg={6} md={6}>
                <div className={classes.buttonContainer}>
                  <Button
                    onClick={() => {
                      this._handleMapButtonClick(locationAddress);
                    }}
                    variant="outlined"
                    className={classnames(
                      classes.outlinedButton,
                      classes.noMarginBottom
                    )}
                    color={'primary'}
                    classes={{ label: classes.buttonLabel }}
                  >
                    {t('View on Map')}
                  </Button>
                </div>
              </GridItem>
              <Grid item lg={12}>
                <h4
                  className={classnames(
                    classes.sectionHeading,
                    classes.headline7
                  )}
                >
                  {t('Contact Details')}
                </h4>
              </Grid>
              <GridItem lg={6} md={6}>
                <Field
                  name={`${locationType}.contactInfo.firstName`}
                  render={({ field, form }) => {
                    return (
                      <Autocomplete
                        value={firstName || ''}
                        getSuggestions={async searchTerm => {
                          return await getContactSuggestions(
                            contacts,
                            searchTerm
                          );
                        }}
                        getSuggestionValue={suggestion => {
                          return _get(suggestion, 'data.firstName', '');
                        }}
                        onSuggestionSelected={this._handleContactSelected}
                        label={t('First Name')}
                        onChange={change => {
                          this._handleChange('firstName', change);
                        }}
                        validateField={handleValidateField}
                        field={field}
                        form={form}
                      />
                    );
                  }}
                />
              </GridItem>
              <GridItem lg={6} md={6}>
                <FastField
                  value={lastName || ''}
                  name={`${locationType}.contactInfo.lastName`}
                  label={t('Last Name')}
                  onChange={handleChange}
                  component={TextFieldInput}
                  validateField={handleValidateField}
                />
              </GridItem>
              <GridItem lg={6} md={6}>
                <Field
                  value={phone || ''}
                  name={`${locationType}.contactInfo.phone`}
                  label={t('Phone Number')}
                  mask={'phone'}
                  onChange={handleChange}
                  component={PhoneField}
                  country={locationAddress.country}
                  validateField={handleValidateField}
                />
              </GridItem>
              <GridItem lg={6} md={6}>
                <FastField
                  value={email || ''}
                  name={`${locationType}.contactInfo.email`}
                  label={t('Email Address')}
                  onChange={handleChange}
                  component={TextFieldInput}
                  validateField={handleValidateField}
                />
              </GridItem>
              <Grid item lg={12}>
                <h4
                  className={classnames(
                    classes.sectionHeading,
                    classes.headline7
                  )}
                >
                  {t('Notes')}
                </h4>
              </Grid>
              <GridItem lg={12}>
                <FastField
                  value={
                    locationType === 'pickup'
                      ? pickupNotes || ''
                      : deliveryNotes || ''
                  }
                  name={
                    locationType === 'pickup'
                      ? `${locationType}.pickupNotes`
                      : `${locationType}.deliveryNotes`
                  }
                  label={t('Notes')}
                  onChange={handleChange}
                  component={TextFieldInput}
                  multiline={true}
                  classes={{
                    root: classes.noMarginBottom
                  }}
                />
              </GridItem>
            </Grid>
            {geoCodedAddress && mapModalOpen && (
              <MapModal
                open={mapModalOpen}
                mapName={`${locationType}Map`}
                address={geoCodedAddress}
                onConfirm={this._handleMapModalClose}
              />
            )}
            {mapError && (
              <MapModalError
                open={mapError}
                onConfirm={() => {
                  this._handleMapModalErrorClose();
                }}
              />
            )}
          </Fragment>
        }
      />
    );
  }
}
Location.contextType = AppContext;

const translatedLocation = withNamespaces()(Location);
export default withStyles(
  combineStyles(
    TypographyStyles,
    LayoutStyles,
    SpacingStyles,
    ButtonStyles,
    LocationStyles
  )
)(translatedLocation);
